import "@szhsin/react-menu/dist/index.css";
import { observer } from "mobx-react";
import React from "react";
import { useAvatarColors } from "../../../app/pages/CvpEnter/useAvatarColors";
import { withWSErrorContainer } from "../../../error/withWSErrorContainer";
import { useReactMenuProps } from "../../../generic-components/common/useReactMenuProps";
import { useResolvedDisplayName } from "../../../user/useResolvedDisplayName";
import { useWhoAmI } from "../../../user/useWhoAmI";
import { useDevice } from "../../../utils/style/useDevice";
import { completeMenuItem } from "../MenuItems/completeMenuItem";
import { DeepFriedMenuItem } from "../../../generic-components/menu/DeepFriedMenuItem";
import { DepartmentTemplate } from "./DepartmentTemplate";
import {
  StyledProfileBtn,
  StyledProfileDropdown,
  StyledReactControlledMenu,
  StyledSimpleProfileDropdown,
} from "./SharedStyles";
import { useProfileDepartmentItems } from "./useProfileDepartmentItems";
import { recordProfileMenuUrlDebugTap } from "../../../url/attachCurrentUrlToBody";

function topbarInitials(displayName: string | null | undefined): string {
  if (!displayName || displayName.includes("@")) return "";
  const parts = displayName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const ProfileMenu = withWSErrorContainer(
  observer(() => {
    const { isTabletOrMobile } = useDevice();
    const { confirmedHasVerifiedProfileInConcern, whoAmI } = useWhoAmI();
    const displayName = useResolvedDisplayName();
    const avatarInitials = topbarInitials(displayName);
    const activeCandidateId =
      whoAmI?.selectedVerifiedProfileInConcernForThisSession ?? null;
    const avatarColors = useAvatarColors();
    const avatarColor =
      activeCandidateId != null ? avatarColors.get(activeCandidateId) : undefined;
    const { btnProps, controlledMenuProps } = useReactMenuProps();
    const { onClick: openMenu, ...restBtnProps } = btnProps;
    const profileActivatorProps = {
      ...restBtnProps,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        recordProfileMenuUrlDebugTap();
        openMenu?.(e);
      },
    };

    return (
      <>
        <input type="hidden" data-testid="id-candidate" value={whoAmI?.idCandidate ?? ''} />
        <input type="hidden" data-testid="identity-provider-user-id" value={whoAmI?.userIdFromIdentityProvider ?? ''} />
        <input type="hidden" data-testid="identity-provider-email" value={whoAmI?.emailFromIdentityProvider ?? ''} />
        <input type="hidden" data-testid="concern-id" value={window.cvpIdConcern ?? ''} />
        
        
        {isTabletOrMobile && (
          <StyledProfileBtn
            role="button"
            className="cvp-profile-btn"
            icon="pi pi-user"
            data-testid="profile-menu-btn"
            {...profileActivatorProps}
          />
        )}

        {!isTabletOrMobile && !!confirmedHasVerifiedProfileInConcern && (
          <StyledProfileDropdown
            role="button"
            aria-label="Profil"
            aria-haspopup="menu"
            data-testid="profile-menu-btn"
            data-avatar-color={avatarColor}
            {...profileActivatorProps}
          >
            <span className="cvp-topbar-avatar" aria-hidden="true">
              {avatarInitials || <i className="pi pi-user" />}
            </span>
            <DepartmentTemplate isTopBar />
          </StyledProfileDropdown>
        )}

        {!isTabletOrMobile && !confirmedHasVerifiedProfileInConcern && (
          <StyledSimpleProfileDropdown
            role="button"
            aria-label="Profil"
            aria-haspopup="menu"
            data-testid="profile-menu-btn"
            {...profileActivatorProps}
          >
            <i className="pi pi-user" aria-hidden="true" />
          </StyledSimpleProfileDropdown>
        )}

        {/* //React menu */}
        <StyledReactControlledMenu {...controlledMenuProps}>
          <ProfileDepReactMenuItems />
        </StyledReactControlledMenu>
      </>
    );
  })
);

interface ProfileDepReactMenuItemsProps {}

const ProfileDepReactMenuItems = withWSErrorContainer(
  observer((props: ProfileDepReactMenuItemsProps) => {
    const profileItems = useProfileDepartmentItems();
    const pItems = profileItems.map((items) => completeMenuItem(items));

    return (
      <>
        {pItems.map((pItem, index) => (
          <DeepFriedMenuItem key={`${index}_${pItem.label}`} pItem={pItem} />
        ))}
      </>
    );
  })
);
